import * as Three from 'three';
import { getPolygonSquare } from 'a_common-folder/utils/geometry';
import { convertMetric } from 'a_common-folder/utils/metric';

import {getVertexRenderCoords, prepareOptions} from "../helper";
import TexturesService from "../libs/textures.service";
import {RESOLUTION_SCALE_HIGH, RESOLUTION_SCALE_LOW, UNIT_COEF} from "../constants";
import Area3D from "./area-3d";
import Teleport3D from "./teleport-3d";
import Wall3D from "./wall-3d";
import {applyInteract, applyOpacity} from "../three-helper";
import {isMetrical} from "../../../service/geo.service";
import {AREA, UNIT_METER} from "a_root-folder/constants";

const defValues = {
    unit: 'mm'
};

const generatePlainPlanData = () => {
    return {
        sceneGraph: {
            unit: defValues.unit,
            layers: {},
            busyResources: { layers: {} },
            width: 0,
            height: 0,
            LODs: {},
            previewMode: false,
            metric: true,
            measurementMode: false
        },
        plan: null,
        boundingBox: null
    }
};

// todo: add methods to expand capabilities of model
class ViewModel3D {
    _planData = generatePlainPlanData();
    _scene = null;
    _texturesPromise = null;
    _interuptTexturesProcessing = false;

    constructor(scene = {}, options = {}) {
        this.sceneGraph = prepareOptions(this._planData.sceneGraph, options);
        this.totalTextures = 0;
        this.loadTexture = 0;
        this._init(scene);
    }

    static updateBoundingBox(planData) {
        let {plan, boundingBox} = planData;
        let newBoundingBox = new Three.Box3().setFromObject(plan);

        if (
            isFinite(newBoundingBox.max.x) &&
            isFinite(newBoundingBox.min.x) &&
            isFinite(newBoundingBox.max.y) &&
            isFinite(newBoundingBox.min.y) &&
            isFinite(newBoundingBox.max.z) &&
            isFinite(newBoundingBox.min.z)
        ) {

            let newCenter = new Three.Vector3(
                (newBoundingBox.max.x - newBoundingBox.min.x) / 2 + newBoundingBox.min.x,
                (newBoundingBox.max.y - newBoundingBox.min.y) / 2 + newBoundingBox.min.y,
                (newBoundingBox.max.z - newBoundingBox.min.z) / 2 + newBoundingBox.min.z
            );

            plan.position.sub(newCenter);

            newBoundingBox.min.sub(newCenter);
            newBoundingBox.max.sub(newCenter);

            boundingBox = newBoundingBox;
        }

        return {plan, boundingBox}
    }

    get planData() {
        return this._planData;
    }

    _init(scene) {
        this._scene = Object.assign({}, scene);
        this._planData.plan = new Three.Object3D();
        this._planData.plan.name = 'plan';

        this._planData.boundingBox = new Three.Box3().setFromObject(this._planData.plan);
        this._planData.boundingBox.name = 'boundingBox';
    }

    _setLayer({id, visible = true, altitude = 0}) {
        this._planData.sceneGraph.layers[id] = {
            id,
            lines: {},
            holes: {},
            areas: {},
            items: {},
            visible,
            altitude
        };

        this._planData.sceneGraph.busyResources.layers[id] = {
            id,
            lines: {},
            holes: {},
            areas: {},
            items: {}
        };
    }

    _sendPostMsg(options={}) {
        options  = JSON.parse(JSON.stringify(options))
        window.postMessage({
            ...options
        }, '*');
    }

    getCountOfTextures(layers) {
        let totalTextures = 0;

        Object.keys(layers).forEach(layerID => {
            let layer = this._scene.layers[layerID];
            totalTextures += Object.values(layer.lines).length + Object.values(layer.areas).length * 2;
        });
        this.totalTextures = totalTextures;
        setTimeout(() => {
            this._sendPostMsg({event: 'totalTexture', total: totalTextures});
        },1)
    }

    getCountOfAreas(layers) {
        let areasTextures = 0;

        Object.keys(layers).forEach(layerID => {
            let layer = this._scene.layers[layerID];
            areasTextures += Object.values(layer.areas).filter((area) => area.vertices.length).length;
        });

        return areasTextures;
    }

    createSceneView(createTextures = true) {
        let promises = [];
        this._interuptTexturesProcessing = false;


        this.getCountOfTextures(this._scene.layers);

        Object.keys(this._scene.layers).forEach(layerID => {
            let layer = this._scene.layers[layerID];

            if (layer.id === this._scene.selectedLayer || layer.visible) {
                // add option to load texture instead of theirs creating
                promises = promises.concat(this._createLayerObjects(layer, createTextures, RESOLUTION_SCALE_HIGH));
            }
        });

        this._texturesPromise = Promise.all(promises)
            .then((data) => {
                if(this._interuptTexturesProcessing) {
                    this._planData = generatePlainPlanData();

                    return Promise.reject('Texture processing was interrupted');
                }

                let result = Object.assign({}, {
                    boundingBox: new Three.Box3().setFromObject(this._planData.plan),
                });

                result.data = this._createTexturesArray(data);

                return result;
            })
            .catch((reason) => {
                console.error(reason);

                return null;
            });

        return {planData: this._planData, promise: this._texturesPromise}
    }

    _createTexturesArray(data) {
        let result = [];

        data.forEach((area) => {
            let areaTextures = area.filter((texture) => texture && texture.type !== 'teleport');
            result.push(...areaTextures);
        });

        return result;
    }

    // todo: need to add scene loading instead of creating
    _loadTexture(layer, area, lines) {
        let {name, vertices} = area;
        const {previewMode} = this._planData.sceneGraph;
        let areaTimePointer = performance.now();

        return new Promise((resolve, reject) => {
            let modelsPromises = [];

            if (this._interuptTexturesProcessing) {
                return reject('Texture processing was interrupted');
            }

            if (!previewMode) {
                modelsPromises.push(this._addCelling(layer, area.id))
            }

            modelsPromises.push(this._addFloor(layer, area.id));
            lines.forEach(line => {
                if (!!~vertices.indexOf(line.vertices[0]) && !!~vertices.indexOf(line.vertices[1])){
                    modelsPromises.push(this._addWall(layer, line.id, null, area.id));
                }
            });

            modelsPromises.push(this._addTeleport(layer, area.id));

            console.info(`Loading of ${name} takes ${performance.now() - areaTimePointer} milliseconds.`);

            Promise.all(modelsPromises).then((values) => resolve(values));
        });
    }

    _createTexture(layer, area, lines, resolution, index, qualityScale) {
        let {name, vertices, properties: {scene: {panorama}}} = area;
        const {previewMode} = this._planData.sceneGraph;

        let texture = new TexturesService(resolution, qualityScale);
        let areaTimePointer = performance.now();

        // todo: we might need to load in a row instead of parallel
        //      to control image loading
        //      add texture loading instead of creating
        return new Promise((resolve, reject) => {
            texture.loadPanorama(panorama, {name, loaded: index + 1, total: Object.values(layer.areas).length}).then(() => {
                let modelsPromises = [];

                if (this._interuptTexturesProcessing) {
                    texture.destroy();

                    return reject('Texture processing was interrupted');
                }

                if (!previewMode) {
                    modelsPromises.push(this._addCelling(layer, area.id, texture))
                }

                modelsPromises.push(this._addFloor(layer, area.id, texture));
                lines.forEach(line => {
                    if (!!~vertices.indexOf(line.vertices[0]) && !!~vertices.indexOf(line.vertices[1])){
                        modelsPromises.push(this._addWall(layer, line.id, texture, area.id));
                    }
                });
                modelsPromises.push(this._addTeleport(layer, area.id));

                texture.destroy();

                console.info("3d Generate room name "  + name + " take " + (performance.now() - areaTimePointer) + " milliseconds.")

                return Promise.all(modelsPromises);
            }).then((values) => resolve(values));
        });
    }

    _createLayerObjects(layer, createTextures = true, resolution) {
        this._setLayer(layer);

        // Import lines
        let lines = Object.values(layer.lines).sort((line1, line2) => {
            return line1.holes.size - line2.holes.size;
        });

        const qualityScale = this._findQualityScale();

        return Object.values(layer.areas)
            .map((area, index) => createTextures
                ? this._createTexture(layer, area, lines, resolution, index, qualityScale)
                : this._loadTexture(layer, area, lines)
            );
    }

    _findQualityScale(){
        //TODO fix for ios not crash on big tours
        let countAreas = this.getCountOfAreas(this._scene.layers);
        let qualityScale = 1;
        if(countAreas > 100){
            qualityScale = 4;
        }else if(countAreas > 90){
            qualityScale = 3.5;
        }else if(countAreas > 80){
            qualityScale = 3;
        }else if(countAreas > 60){
            qualityScale = 2.75;
        }else if(countAreas > 40){
            qualityScale = 1.75;
        }else if(countAreas > 30){
            qualityScale = 1.5;
        }

        return qualityScale;
    }

    _addFloor(layer, areaID, texture) {
        if (this._planData.sceneGraph.busyResources.layers[layer.id].areas[areaID]) {
            setTimeout(() => this._addFloor(layer, areaID, texture), 100);
            return;
        }

        this._planData.sceneGraph.busyResources.layers[layer.id].areas[areaID] = true;

        let area = layer.areas[areaID];
        let coef = UNIT_COEF[this._scene.unit];

        let area3D = new Area3D('floor');

        return !area.vertices.length ? null : area3D.render3D(area, layer, this._scene, texture).then(({obj:area3DLOD, data}) => {
            if (area3DLOD instanceof Three.LOD) {
                this._planData.sceneGraph.LODs[areaID] = area3DLOD;
            }

            let pivot = new Three.Object3D();
            pivot.name = 'area';
            pivot.areaID = areaID;
            pivot.identifier = areaID;
            pivot.layerID = layer.id;
            pivot.layerName = layer.name;
            pivot.areaName = area.name;
            pivot.hide = false;
            pivot.add(area3DLOD);
            pivot.position.y = layer.altitude * coef;
            this._planData.plan.add(pivot);
            this._planData.sceneGraph.layers[layer.id].areas[areaID] = pivot;

            // applyInteract(pivot, interactFunction);

            let opacity = layer.opacity || 1;
            let src = data ? data.src : null;
            if (area.selected) {
                opacity = 1;
            }

            applyOpacity(pivot, opacity);
            this._planData.sceneGraph.busyResources.layers[layer.id].areas[areaID] = false;

            this.loadTexture++;
            this._sendPostMsg({event: 'parseTexture', total: this.totalTextures, loaded: this.loadTexture, urlBase64: true});

            // return here if smthing needed
            return {id:areaID, src, type: 'floor', layerName: layer.name, areaName: area.name}
        });
    }
    
    _getHeightTeleport(area) {
        if(this.sceneGraph.measurementMode) return area.properties.scene.height_lens.height / 10;

        return 100;
    }

    _addTeleport(layer, areaID) {
        let {previewMode, measurementMode, metric} = this.sceneGraph;

        let area = layer.areas[areaID];
        let coef = UNIT_COEF[this._scene.unit];
        let height = this._getHeightTeleport(area);
        let {name, camera: {x, y}} = area.properties.scene;
        let teleport3D = new Teleport3D();

        if (!area.vertices.length) return null;

        const polygon = area.vertices.map((vid) => {
            const vertex = layer.vertices[vid];
            const {x, y} = getVertexRenderCoords(vertex, this._scene.unit);

            return [x, y];
        });

        let areaSize = getPolygonSquare(polygon);
        let metricObj = convertMetric(areaSize / 10000, AREA, metric, UNIT_METER, true);

        const areaString = `${metricObj.value} ${metricObj.unit}${String.fromCharCode(0xb2)}`;
        // Oprimaze for big tour on ios
        let optimazerMode = false;
        const qualityScale = this._findQualityScale();
        if(qualityScale >= 3){
            previewMode = true;
            optimazerMode = true;
        }
        const tripodHeight = this._getHeightTeleport(area);
        // const tripodHeight = 280;

        // return  teleport3D.render3D(height, name, areaString, previewMode, optimazerMode, measurementMode).then((pivot) => {
        return  teleport3D.render3D(tripodHeight, name, areaString, previewMode, optimazerMode, measurementMode).then((pivot) => {
            pivot.areaID = areaID;
            pivot.identifier = areaID;
            pivot.layerID = layer.id;
            pivot.hide = false;
            pivot.renderOrder = 1;

            pivot.position.x = x * coef;
            pivot.position.z = -y * coef;
            pivot.position.y = layer.altitude * coef + this._getHeightTeleport(area) / 2;
            // pivot.position.y = layer.altitude * coef;
            this._planData.plan.add(pivot);
            // applyInteract(pivot, interactFunction);

            // todo: add floors opacity switch
            let opacity = layer.opacity || 1;
            if (area.selected) {
                opacity = 1;
            }

            // applyOpacity(pivot, opacity);
            return {id:areaID, type: 'teleport'}
        });

        // return !area.vertices.length ? null : teleport3D.render3D(height, name, areaString, previewMode).then((pivot) => {
        //     pivot.areaID = areaID;
        //     pivot.identifier = areaID;
        //     pivot.layerID = layer.id;
        //     pivot.hide = false;
        //     pivot.renderOrder = 1;
        //
        //     pivot.position.x = x * coef;
        //     pivot.position.z = -y * coef;
        //     pivot.position.y = layer.altitude * coef + 50;
        //
        //     this._planData.plan.add(pivot);
        //
        //     // applyInteract(pivot, interactFunction);
        //
        //     // todo: add floors opacity switch
        //     let opacity = layer.opacity || 1;
        //     if (area.selected) {
        //         opacity = 1;
        //     }
        //
        //     // applyOpacity(pivot, opacity);
        //     return {id:areaID, type: 'teleport'}
        // });
    }

    _addCelling(layer, areaID, texture) {
        let area = layer.areas[areaID];
        let coef = UNIT_COEF[this._scene.unit];
        let height = area.properties.scene.ceiling * coef;
        let ceiling3D = new Area3D('ceiling');

        return !area.vertices.length ? null : ceiling3D.render3D(area, layer, this._scene, texture)
            .then(({obj:ceiling3DObj, data}) => {
                let pivot = new Three.Object3D();

                pivot.name = 'ceiling';
                pivot.areaID = areaID;
                pivot.identifier = areaID;
                pivot.layerID = layer.id;
                pivot.layerName = layer.name;
                pivot.areaName = area.name;
                pivot.hide = false;
                pivot.add(ceiling3DObj);

                pivot.position.y = layer.altitude * coef + height;
                this._planData.plan.add(pivot);

                let opacity = layer.opacity || 1;
                let src = data ? data.src : null;
                if (area.selected) {
                    opacity = 1;
                }

                applyOpacity(pivot, opacity);

                this.loadTexture++;
                this._sendPostMsg({event: 'parseTexture', total: this.totalTextures, loaded: this.loadTexture, urlBase64: true});

                return {id:areaID, src, type: 'ceiling', layerName: layer.name, areaName: area.name}
            }).catch((err) => {
                console.error(err)
            });
    }

    _addWall(layer, lineID, texture, areaID) {
        if (this._planData.sceneGraph.busyResources.layers[layer.id].lines[lineID]) {
            setTimeout(() => this._addWall(layer, lineID, texture, areaID), 100);
            return;
        }
        this._planData.sceneGraph.busyResources.layers[layer.id].lines[lineID] = true;

        let area = layer.areas[areaID];
        let line = layer.lines[lineID];
        const sceneUnit = this._scene.unit,
            coef = UNIT_COEF[sceneUnit];

        // First of all I need to find the vertices of this line
        let vertex0 = getVertexRenderCoords(layer.vertices[line.vertices[0]], sceneUnit);
        let vertex1 = getVertexRenderCoords(layer.vertices[line.vertices[1]], sceneUnit);

        if (vertex0.x > vertex1.x) {
            let tmp = vertex0;
            vertex0 = vertex1;
            vertex1 = tmp;
        }

        let wall3D = new Wall3D();

        return wall3D.render3D(line, layer, this._scene, texture).then(({obj:line3D,data}) => {
            if (line3D instanceof Three.LOD) {
                this._planData.sceneGraph.LODs[line.id] = line3D;
            }

            if(!Object.keys(line3D).length) return ;
            let pivot = new Three.Object3D();
            pivot.name = 'wall';
            pivot.identifier = line.id;
            pivot.areaID = areaID;
            pivot.layerID = layer.id;
            pivot.layerName = layer.name;
            pivot.areaName = area.name;
            pivot.hide = false;
            pivot.visible = (line.properties.visible || typeof line.properties.visible == 'undefined') ? true : false;
            pivot.add(line3D);

            pivot.position.x = vertex0.x;
            pivot.position.y = layer.altitude * coef;
            pivot.position.z = -vertex0.y;

            this._planData.plan.add(pivot);
            this._planData.sceneGraph.layers[layer.id].lines[lineID] = pivot;

            let opacity = layer.opacity;
            let src = data ? data.src : null;

            if (line.selected) {
                opacity = 1;
            }
            applyOpacity(pivot, opacity);
            this._planData.sceneGraph.busyResources.layers[layer.id].lines[lineID] = false;
            this.loadTexture++;
            this._sendPostMsg({event: 'parseTexture', total: this.totalTextures, loaded: this.loadTexture, urlBase64: true});

            // return here if smthing needed
            return {id:lineID, src, type: 'wall', layerName: layer.name, areaName: area.name}
        });
    }

    // todo: need to be reviewed on memory leaks
    destroy() {
        this._planData = generatePlainPlanData();
        this.interruptTexturesProcessing();
    }

    interruptTexturesProcessing() {
        this._interuptTexturesProcessing = true;
    }
}

export default ViewModel3D;
